import { IViolation, Collection } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { SingleStreamOutputTransformation } from './single-output-stream.transformation';
import { ConditionExpression } from './condition-expression';
import { convertColumnRefToDataFlowColumn, IDataFlowArguments, convertParametersToArguments, convertCacheSinksToArguments } from './dataflow-expression-utilities';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { DataFlowExpression } from './dataflowexpression';

import { format, sanitizeAndFormatString, stringIsNullOrEmpty } from '../../shared/utils/string.util';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowStream } from 'app/model/dataflow/transformation';
import { updateIdentifiers, asyncUpdateIdentifiers } from './dataflow-expression-update-identifiers';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { GlobalVariables } from 'app/services/globals';

export enum BroadcastType {
    OFF = 'off',
    AUTO = 'auto',
    FIXED = 'fixed'
}

@Metamodel.model({
    name: 'basemultiinputstreamtransformation', // As this is abstract this will never be used
    abstract: true
})
export abstract class BaseMultiInputStreamTransformation extends SingleStreamOutputTransformation {
    public transformationLabel: string = '';

    @Metamodel.attribute({
        constraint: {
            validator:  BaseMultiInputStreamTransformation.validateBroadcastType
        }
    })
    public broadcastType: BroadcastType = BroadcastType.AUTO;
    public broadcastLeftStream: boolean;
    public broadcastRightStream: boolean;
    public get conditionExpressions(): ConditionExpression[] {
        // overwrite getConditionExpressions/setConditionExpressions if you need to customize your own expression list
        return this.getConditionExpressions();
    }
    public set conditionExpressions(value: ConditionExpression[]) {
        this.setConditionExpressions(value);
    }
    public hasNonEqualityConditionExpression: boolean = false;

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => this._getStreamLabel(stringResources.DataFlowLeftHeaderLabel, 0),
            value: () => this._getStreamCount(0)
        },
        {
            name: () => this._getStreamLabel(stringResources.DataFlowRightHeaderLabel, 1),
            value: () => this._getStreamCount(1)
        }
    ];

    public inspectDetailsOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_Column_FedBy,
            value: (column) => column.ownerTransformation.name
        },
        {
            name: () => format(stringResources.UsedInTransformationLabel, this.transformationLabel),
            icon: this._getUsedInTransformationIcon.bind(this)
        }
    ];

    @Metamodel.attribute({
        type: ConditionExpression.MODEL,
        collection: Collection.Array,
        constraint: {
            validator:  BaseMultiInputStreamTransformation.validateConditionExpressions
        }
    })
    protected _conditionExpressions: ConditionExpression[] = [];

    protected get usesConditionExpressions(): boolean {
        return true;
    }

    public static validateConditionExpressionTypes(transformation: BaseMultiInputStreamTransformation): IViolation[] | undefined {
        if (transformation.conditionExpressions.length > 0) {
            const violations: IViolation[] = [];
            let hasNonEquiJoinBroadcastError = false;
            transformation.conditionExpressions.forEach((conditionExpression) => {
                if (conditionExpression.hasComplexOrArrayType()) {
                    violations.push({
                        context: conditionExpression,
                        message: () => stringResources.DataFlowEditorErrorComplexType
                    });
                } else if (!conditionExpression.isTypesCompatible()) {
                    violations.push({
                        context: conditionExpression,
                        message: () => format(stringResources.DataFlowEditorErrorTypeUncompatibleWithLeftSide,
                            conditionExpression.rightExpression.returnType.typeString,
                            conditionExpression.leftExpression.returnType.typeString)
                    });
                }
                hasNonEquiJoinBroadcastError = hasNonEquiJoinBroadcastError ||
                    (conditionExpression.isNonEqualityCondition &&
                    !(transformation.broadcastType === BroadcastType.FIXED && (transformation.broadcastLeftStream || transformation.broadcastRightStream)));
            });
            if (hasNonEquiJoinBroadcastError) {
                violations.push({
                    context: transformation,
                    message: () => stringResources.NonEquiCondition_BroadcastError
                });
            }
            return violations;
        }

        return undefined;
    }

    public static validateConditionExpressionExists(transformation: BaseMultiInputStreamTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        transformation.conditionExpressions.forEach((expression) => {
            if (stringIsNullOrEmpty(expression.leftExpression.expression) || stringIsNullOrEmpty(expression.rightExpression.expression)) {
                violations.push({
                    context: transformation,
                    message: () => sanitizeAndFormatString(stringResources.DataFlowErrorConditionExpressionIsEmpty, transformation.name)
                });
            }
        });

        return violations.length > 0 ? violations : undefined;
    }

    public static validateConditionExpressions(transformation: BaseMultiInputStreamTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.usesConditionExpressions) {
            const expressionTypesErrors = BaseMultiInputStreamTransformation.validateConditionExpressionTypes(transformation);
            const expressionExistsErrors = BaseMultiInputStreamTransformation.validateConditionExpressionExists(transformation);

            if (expressionTypesErrors) {
                violations.push(...expressionTypesErrors);
            }
            if (expressionExistsErrors) {
                violations.push(...expressionExistsErrors);
            }
        }

        return violations.length > 0 ? violations : undefined;
    }

    public static validateBroadcastType(transformation: BaseMultiInputStreamTransformation): IViolation[] {
        const violations: IViolation[] = [];
        if (transformation.broadcastType === BroadcastType.FIXED && !transformation.broadcastLeftStream && !transformation.broadcastRightStream) {
            violations.push({
                context: transformation,
                message: () => stringResources.MultiInputStreamTransformationBroadcastError
            });
        }

        return violations;
    }

    public inputStreamValidate(): IViolation[] | undefined {
        if (this && this.inputStreams) {
            if (this.inputStreams.length < 2) {
                return [
                    {
                        context: this,
                        message: () => stringResources.DataFlowErrorInputStreamsNotSet
                    }
                ];
            } else {
                return this.validateInputColumns();
            }
        }
        return undefined;
    }

    public validateInputColumns(): IViolation[] | undefined {
        const columnSet = new Set<DataFlowColumn>();
        const invalidColumnMaps = new Map<DataFlowStream, DataFlowColumn[]>();
        const violations: IViolation[] = [];
        this.distinctInputColumnRefs.forEach((columnRef) => {
            if (columnSet.has(columnRef.column)) {
                if (!invalidColumnMaps.has(columnRef.inputStream)) {
                    invalidColumnMaps.set(columnRef.inputStream, []);
                }
                const invalidCols = invalidColumnMaps.get(columnRef.inputStream);
                if (invalidCols.indexOf(columnRef.column) < 0) {
                    invalidCols.push(columnRef.column);
                }
            } else {
                columnSet.add(columnRef.column);
            }
        });

        invalidColumnMaps.forEach((value: DataFlowColumn[], key: DataFlowStream) => {
            violations.push({
                message: () => sanitizeAndFormatString(stringResources.JoinDuplicateColumnsErrorFormat,
                    value.map((col) => col.name).join(','), key.producer.name
                ),
                context: this
            });
        });

        return violations;
    }

    public getExpressions(): DataFlowExpression[] {
        const dataFlowExpressions: DataFlowExpression[] = [];

        this.conditionExpressions
            .forEach((conditionExpressions) => {
                if (conditionExpressions.leftExpression) {
                    dataFlowExpressions.push(conditionExpressions.leftExpression);
                }
                if (conditionExpressions.rightExpression) {
                    dataFlowExpressions.push(conditionExpressions.rightExpression);
                }
            });

        return dataFlowExpressions;
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        const availableArguments: IDataFlowArguments = {
            dataFlowColumns: convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs),
            dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
            dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
            dataFlowTransientColumns: convertColumnRefToDataFlowColumn(this.transientColumnRefs),
            dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
        };
        this.conditionExpressions.forEach((conditionExpression) => {
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers(availableArguments, conditionExpression.leftExpression, this);
                asyncUpdateIdentifiers(availableArguments, conditionExpression.rightExpression, this);
            } else {
                updateIdentifiers(availableArguments, conditionExpression.leftExpression);
                updateIdentifiers(availableArguments, conditionExpression.rightExpression);
            }
        });
    }

    protected getConditionExpressions(): ConditionExpression[] {
        return this._conditionExpressions;
    }

    protected setConditionExpressions(value: ConditionExpression[]): void {
        this._conditionExpressions = value;
    }

    private _getStreamLabel(headerLabel: string, index: number): string {
        return `${headerLabel}: ${this.inputStreams[index] ? this.inputStreams[index].qualifiedName : ''}`;
    }

    private _getStreamCount(index: number): string {
        return this.inputStreams[index] ? `${this.inputStreams[index].producer.outputColumns().length}` : '';
    }

    private _getUsedInTransformationIcon(column: DataFlowColumn): string {
        const columnRef = this.distinctInputColumnRefs.find((e) => e.column === column);
        const isUsedInTransformation = this.conditionExpressions.reduce((result: DataFlowExpression[], expression) => result.concat(expression.leftExpression, expression.rightExpression), [])
            .find((expression) => expression.argumentsUsed.dataFlowColumns.find((x) => x.column.name === (columnRef ? columnRef.name : column.name)) != null) != null;
        return isUsedInTransformation ? 'svg-i-20x20-checkmark' : '';
    }
}
